import * as Sentry from '@sentry/browser'
import { option as O } from 'fp-ts'
import * as t from 'io-ts'
import { parseHostname } from '../lib/parseHostname'
import { Intl } from './intl'
import { PublicationMatcher, _casePublication } from './publication'
import * as F from 'fp-ts'

/**
 * Report an error to Sentry when we reach a point where we use the default
 * string. It means something is horribly broken.
 */
function traceUsingDefault(def: unknown) {
  Sentry.withScope((scope) => {
    scope.setExtras({ def })
    Sentry.captureMessage(`MultiLocaleString: using default`)
  })
}

/**
 * A 'MultiLocaleString' is essentially a Map<string, string> where keys are
 * locales (eg. "de") and values are plain strings. Values may be empty strings.
 */
export const MultiLocaleString = t.record(t.string, t.union([t.undefined, t.null, t.string]))
export type MultiLocaleString = t.TypeOf<typeof MultiLocaleString>

export const emptyMultiLocaleString: MultiLocaleString = {}

/**
 * Concatenate two or more MultiLocaleString's. Non-empty values in later objects
 * overwrite previous values.
 */
export const concatMultiLocaleString = (x: MultiLocaleString, ...xs: MultiLocaleString[]): MultiLocaleString =>
  Object.fromEntries([x, ...xs].flatMap((mls) => Object.entries(mls)).filter(([, v]) => !!v))

const parentLocale = (locale: string): string => locale.split('-')[0]

export const lookupString = (locale: string, mls: MultiLocaleString): O.Option<string> => {
  // This fallback logic is already implemented inside intl-messageformat, but not exposed :(
  // We use a simplified version here.
  //
  // Note: We intentionally don't use defaultLocale!
  return F.function.pipe(
    O.fromNullable(mls[locale] || null),
    O.alt(() => O.fromNullable(mls[parentLocale(locale)] || null)),
  )
}

export const optionMultiLocaleString = (intl: Intl, mls: MultiLocaleString): O.Option<string> => {
  return lookupString(intl.locale, mls)
}

const localeFallbackChain = /*@__PURE__*/ (() => {
  /*
   * We inlude "it" here even though it's not any of the supported UI locales. However
   * the ballots in Ticino come only in the italian language, and without "it" in the chain
   * the 'pickMultiLocaleString' function would use the default string instead.
   */
  const byPublication: PublicationMatcher<string[]> = {
    '24heures': ['fr', 'de', 'it'],
    bazonline: ['de', 'fr', 'it'],
    bernerzeitung: ['de', 'fr', 'it'],
    derbund: ['de', 'fr', 'it'],
    lematin: ['fr', 'de', 'it'],
    tagesanzeiger: ['de', 'fr', 'it'],
    tdg: ['fr', 'de', 'it'],
    firebaseapp: ['de', 'fr', 'it'],
    web: ['de', 'fr', 'it'],
    landbote: ['de', 'fr', 'it'],
    zsz: ['de', 'fr', 'it'],
    zuonline: ['de', 'fr', 'it'],
  }

  return _casePublication(byPublication, parseHostname().publicationName) || ['de', 'fr', 'it']
})()

export const pick = <T>(locale: string, mls: MultiLocaleString, def: T): string | T =>
  F.function.pipe(
    lookupString(locale, mls),
    F.option.foldW(
      () => {
        /*
         * If the primary locale is not available, try one of the locales in
         * the fallback chain. If none of those is available either, use the
         * default string.
         *
         * Idea: another improvement to consider would be to try any of the
         * available locales before using the default string, eg. use the locale
         * `Object.keys(mls)[0]`. This should not fail unless the MultiLocaleString
         * is really empty, which is highly unlikely.
         */

        return F.function.pipe(
          localeFallbackChain.reduce<O.Option<string | T>>(
            (o, locale) =>
              F.function.pipe(
                o,
                O.altW(() => O.fromNullable(mls[locale] || null)),
              ),
            O.none,
          ),
          F.option.getOrElseW(() => {
            traceUsingDefault(def)
            return def
          }),
        )
      },
      (x) => x,
    ),
  )

/**
 * Warning: if the string is not available in the primary locale, this function will
 * use any string from the locale fallback chain. It should therefore not be used when
 * displaying the text in another locale is not desired (such as when displaying the
 * lead texts).
 */
export const pickMultiLocaleString = <T>(intl: Intl, mls: MultiLocaleString, def: T): string | T =>
  pick(intl.locale, mls, def)

/**
 * Select a string based on a prioritized list of 'MultiLocaleString' objects. This function
 * provides a more fine-grained control over which string in which locale (primary, fallback)
 * to pick.
 *
 * Example:
 *
 *   selectMultiLocaleString(intl, "<XXX>", [
 *     { mls: doc.detailName, locale: "primary" }
 *     { mls: doc.name, locale: "primary" }
 *     { mls: doc.detailName, locale: "fallback" }
 *     { mls: doc.name, locale: "fallback" }
 *   ])
 */
export const selectMultiLocaleString = <T>(
  intl: Intl,
  def: T,
  chain: Array<{ mls: null | undefined | MultiLocaleString; locale: 'primary' | 'fallback' }>,
): string | T =>
  F.function.pipe(
    chain.reduce<O.Option<string | T>>(
      (o, { mls, locale }) =>
        F.function.pipe(
          O.fromNullable(mls),
          O.chain((mls) =>
            F.function.pipe(
              o,
              O.altW(() =>
                ({
                  primary: () => lookupString(intl.locale, mls),
                  fallback: () =>
                    localeFallbackChain.reduce<O.Option<string | T>>(
                      (o, locale) =>
                        F.function.pipe(
                          o,
                          O.altW(() => O.fromNullable(mls[locale] || null)),
                        ),
                      O.none,
                    ),
                })[locale](),
              ),
            ),
          ),
        ),
      O.none,
    ),
    F.option.getOrElseW(() => {
      traceUsingDefault(def)
      return def
    }),
  )
